import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, PlayCircle, Loader2, Sparkles, ClipboardCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

/**
 * Phase 3(a) — Admin Doctor Reviews.
 *
 * Renders 8 audit tools with fixed checklists, admin notes, and either:
 *  - a "Run" button that computes deterministic output synchronously (calc), or
 *  - a "Run AI analysis" button that enqueues an async LLM job (ai), polled
 *    every 3 s until it lands (or fails).
 *
 * Route: /admin/doctors/:doctorId/reviews
 */
export default function AdminDoctorReviews() {
    const { doctorId } = useParams();
    const nav = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [savingKey, setSavingKey] = useState("");
    const [runningTool, setRunningTool] = useState("");
    const [pendingJobs, setPendingJobs] = useState({}); // tool -> job_id

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/admin/doctors/${doctorId}/reviews`);
            setData(r.data);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load reviews"));
        } finally {
            setLoading(false);
        }
    }, [doctorId]);

    useEffect(() => {
        load();
    }, [load]);

    // Poll pending AI jobs
    useEffect(() => {
        const ids = Object.entries(pendingJobs);
        if (!ids.length) return undefined;
        const t = setInterval(async () => {
            for (const [tool, jobId] of ids) {
                try {
                    const r = await api.get(`/admin/doctors/reviews/jobs/${jobId}`);
                    const status = r.data?.job?.status;
                    if (status === "completed" || status === "failed") {
                        setPendingJobs((p) => {
                            const copy = { ...p };
                            delete copy[tool];
                            return copy;
                        });
                        if (status === "completed") toast.success(`${tool}: analysis ready.`);
                        else toast.error(`${tool}: analysis failed.`);
                        await load();
                    }
                } catch {
                    // ignore transient errors
                }
            }
        }, 3000);
        return () => clearInterval(t);
    }, [pendingJobs, load]);

    if (loading) {
        return <AdminShell><div className="p-6 text-[#17212B]/60">Loading reviews…</div></AdminShell>;
    }
    if (!data) return <AdminShell><div className="p-6">Not found.</div></AdminShell>;

    const runTool = async (tool) => {
        setRunningTool(tool);
        try {
            const r = await api.post(`/admin/doctors/${doctorId}/reviews/${tool}/run`);
            const j = r.data;
            if (j.kind === "ai") {
                setPendingJobs((p) => ({ ...p, [tool]: j.job_id }));
                toast.info("AI analysis queued — polling for results…");
            } else {
                toast.success("Analysis computed.");
                await load();
            }
        } catch (e) {
            toast.error(errMsg(e, "Run failed"));
        } finally {
            setRunningTool("");
        }
    };

    const toggleChecklist = async (tool, item, checked) => {
        setSavingKey(`${tool}:${item}`);
        try {
            await api.put(`/admin/doctors/${doctorId}/reviews/${tool}/checklist`,
                { item_key: item, checked });
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSavingKey("");
        }
    };

    const saveNotes = async (tool, notes) => {
        setSavingKey(`${tool}:notes`);
        try {
            await api.put(`/admin/doctors/${doctorId}/reviews/${tool}/notes`, { notes });
            toast.success("Notes saved.");
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSavingKey("");
        }
    };

    return (
        <AdminShell>
            <div className="p-6 max-w-5xl mx-auto" data-testid="admin-reviews-page">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => nav(`/admin/doctors`)}
                            className="text-sm text-[#0F766E] hover:underline inline-flex items-center gap-1"
                            data-testid="admin-reviews-back"
                        >
                            <ArrowLeft size={14} /> Back to doctors
                        </button>
                    </div>
                    <button
                        onClick={load}
                        className="text-sm text-[#17212B]/60 hover:text-[#123B5D] inline-flex items-center gap-1"
                        data-testid="admin-reviews-refresh"
                    >
                        <RefreshCcw size={13} /> Refresh
                    </button>
                </div>

                <div className="mb-6">
                    <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Admin Review Tools</div>
                    <h1 className="text-2xl font-bold text-[#123B5D]">8 audit modules for this doctor</h1>
                    <p className="text-sm text-[#17212B]/60 mt-1">
                        Each tool has a fixed checklist you can tick off, free-form notes, and either a deterministic report
                        or an AI-driven analysis. All actions are audit-logged.
                    </p>
                </div>

                <div className="space-y-4">
                    {data.tool_order.map((tool) => {
                        const meta = data.tools_meta[tool];
                        const state = data.state[tool] || {};
                        const checklist = state.checklist || {};
                        const isAI = meta.kind === "ai";
                        const isPending = !!pendingJobs[tool];
                        return (
                            <Card key={tool} className="border-[#123B5D]/10" data-testid={`review-card-${tool}`}>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center justify-between gap-3">
                                        <span className="text-base flex items-center gap-2 text-[#123B5D]">
                                            {isAI ? <Sparkles size={16} className="text-[#0F766E]" />
                                                 : <ClipboardCheck size={16} className="text-[#0F766E]" />}
                                            {meta.label}
                                        </span>
                                        <Badge className={isAI ? "bg-[#0F766E]/10 text-[#0F766E]" : "bg-[#123B5D]/10 text-[#123B5D]"}>
                                            {isAI ? "AI (async)" : "Computed"}
                                        </Badge>
                                    </CardTitle>
                                    <p className="text-xs text-[#17212B]/60 mt-1">{meta.description}</p>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Checklist */}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 mb-2">Checklist</div>
                                        <div className="space-y-1.5">
                                            {(meta.checklist || []).map((item) => {
                                                const key = `${tool}:${item}`;
                                                return (
                                                    <label key={item} className="flex items-center gap-2 text-sm text-[#17212B]/80">
                                                        <Checkbox
                                                            checked={!!checklist[item]}
                                                            disabled={savingKey === key}
                                                            onCheckedChange={(v) => toggleChecklist(tool, item, !!v)}
                                                            data-testid={`review-check-${tool}-${item.replace(/\s+/g, '_')}`}
                                                        />
                                                        <span>{item}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Run + result */}
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <Button
                                                onClick={() => runTool(tool)}
                                                disabled={runningTool === tool || isPending}
                                                data-testid={`review-run-${tool}`}
                                            >
                                                {isPending || runningTool === tool ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />}
                                                {isPending ? " Analyzing…" : isAI ? " Run AI analysis" : " Compute report"}
                                            </Button>
                                            {state.last_run_at ? (
                                                <span className="text-xs text-[#17212B]/50">
                                                    Last run · {state.last_run_at.split("T")[0]}
                                                </span>
                                            ) : null}
                                        </div>

                                        {state.last_result ? (
                                            <pre
                                                className="mt-3 text-xs bg-[#F5F8FA] border border-[#123B5D]/10 rounded-md p-3 overflow-x-auto max-h-[280px]"
                                                data-testid={`review-result-${tool}`}
                                            >
                                                {JSON.stringify(state.last_result, null, 2)}
                                            </pre>
                                        ) : null}
                                    </div>

                                    {/* Notes */}
                                    <NotesBlock
                                        tool={tool}
                                        value={state.notes || ""}
                                        onSave={(v) => saveNotes(tool, v)}
                                        saving={savingKey === `${tool}:notes`}
                                    />
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </AdminShell>
    );
}

function NotesBlock({ tool, value, onSave, saving }) {
    const [text, setText] = useState(value);
    const [dirty, setDirty] = useState(false);
    useEffect(() => { setText(value); setDirty(false); }, [value]);
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 mb-1">Admin notes</div>
            <Textarea
                rows={2}
                value={text}
                onChange={(e) => { setText(e.target.value); setDirty(true); }}
                placeholder="Optional notes — visible only to admins."
                maxLength={5000}
                data-testid={`review-notes-${tool}`}
            />
            {dirty ? (
                <button
                    onClick={() => onSave(text)}
                    disabled={saving}
                    className="mt-1 text-xs text-[#0F766E] font-semibold hover:underline"
                    data-testid={`review-notes-save-${tool}`}
                >
                    {saving ? "Saving…" : "Save notes"}
                </button>
            ) : null}
        </div>
    );
}
